import type { PromoCampaignModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import { Link } from '@tanstack/react-router'
import { ArrowRightIcon } from 'lucide-react'

import { UsernameById } from '@/entities/user'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { createSRTColumnHelper } from '@/shared/components/master-table'
import { Badge } from '@/shared/components/ui/badge'
import { linkButtonVariants } from '@/shared/components/ui/link-button'
import { Tag } from '@/shared/components/ui/tag'
import { getUTCDate, intlFormat } from '@/shared/tools/dates'

const columnHelper = createSRTColumnHelper<PromoCampaignModel>()
export default function getColumn(t: TFunction) {
	return [
		columnHelper.accessor('name', {
			header: t('promo_campaign_name'),
			Cell: ({ cellValue, row }) => (
				<Link
					className={linkButtonVariants({ variant: 'brand' })}
					to='/p/promo/$id'
					params={{ id: row.original.id }}
				>
					{cellValue}
				</Link>
			),
		}),
		columnHelper.accessor('is_active', {
			size: 80,
			header: t('app.status'),
			Cell: ({ cellValue }) => {
				if (cellValue) {
					return <Tag variant='success'>Live</Tag>
				} else {
					return <Tag variant='destructive'>Paused</Tag>
				}
			},
		}),
		columnHelper.accessor((row) => row.pos_ids?.length, {
			size: 150,
			id: 'pos_ids',
			header: t('sales_unit'),
			Cell: ({ cellValue }) => <NumberField value={cellValue} />,
		}),
		columnHelper.accessor((row) => row.date_start + ' - ' + row.date_end, {
			size: 200,
			id: 'period',
			header: t('general_period'),
			Cell: ({ row }) => {
				const { date_start, date_end } = row.original

				return (
					<div className='flex items-center space-x-1 font-normal'>
						{date_start && (
							<span>{intlFormat(getUTCDate(new Date(date_start)))}</span>
						)}
						{(date_start || date_end) && (
							<ArrowRightIcon className='size-4 shrink-0' />
						)}
						{date_end && (
							<span>{intlFormat(getUTCDate(new Date(date_end)))}</span>
						)}
					</div>
				)
			},
		}),
		columnHelper.accessor('source', {
			size: 100,
			header: t('creation_source'),
			Cell: ({ cellValue }) => (
				<Badge className='text-nowrap capitalize fade-in'>{cellValue}</Badge>
			),
		}),
		columnHelper.accessor(
			(row) => (row.created_on ? new Date(row.created_on).getTime() : 0),
			{
				id: 'created_on',
				size: 200,
				header: t('general_created'),
				Cell: ({ row }) => {
					const { user_created, created_on } = row.original

					return (
						user_created && (
							<div className='text-sm'>
								<strong>
									by <UsernameById userId={user_created} />
								</strong>{' '}
								<br />
								<span className='text-muted-foreground'>
									{created_on ? intlFormat(new Date(created_on)) : ''}
								</span>
							</div>
						)
					)
				},
			}
		),
		columnHelper.accessor(
			(row) => (row.modified_on ? new Date(row.modified_on).getTime() : 0),
			{
				id: 'created_on',
				size: 200,
				header: t('general_last_updated'),
				Cell: ({ row }) => {
					const { user_modified, modified_on } = row.original

					return (
						user_modified && (
							<div className='text-sm'>
								<strong>
									by <UsernameById userId={user_modified} />
								</strong>{' '}
								<br />
								<span className='text-muted-foreground'>
									{modified_on ? intlFormat(new Date(modified_on)) : ''}
								</span>
							</div>
						)
					)
				},
			}
		),
	]
}
